// Masks panel (§11.4): add linear / radial / brush masks (max 8), select one to edit on the photo, its
// adjustments, invert, delete and the overlay (O). M22: colour-range and object masks ("isolate an
// ingredient"): pick / paint in / paint out / limit, quick presets, Range, Softness, Edge refine.
import { Slider } from '../controls/Slider';
import { BrushControls } from './BrushControls';
import { useActive } from './useActive';
import { useUi } from '../../state/ui';
import { useT, type MessageKey } from '../../i18n';
import { errorKey } from '../../i18n/errors';
import { MAX_MASKS, newMask } from '../../features/localTools';
import { COLOUR_PRESETS } from '../../features/colourMask';
import { useObjectMasks } from '../../features/background/objectMask';
import type {
  ColourGeom,
  ColourPreset,
  LocalMask,
  MaskAdj,
  MaskLimit,
  ObjectGeom,
  RadialGeom,
} from '../../model/types';

const ADJ: {
  key: keyof MaskAdj;
  label: MessageKey;
  min: number;
  max: number;
  step: number;
  digits?: number;
}[] = [
  { key: 'exposure', label: 'exposure', min: -4, max: 4, step: 0.01, digits: 2 },
  { key: 'contrast', label: 'contrast', min: -100, max: 100, step: 1 },
  { key: 'highlights', label: 'highlights', min: -100, max: 100, step: 1 },
  { key: 'shadows', label: 'shadows', min: -100, max: 100, step: 1 },
  { key: 'temp', label: 'temp', min: -100, max: 100, step: 1 },
  { key: 'tint', label: 'tint', min: -100, max: 100, step: 1 },
  { key: 'saturation', label: 'saturation', min: -100, max: 100, step: 1 },
  { key: 'vibrance', label: 'vibrance', min: -100, max: 100, step: 1 },
  { key: 'hue', label: 'hue', min: -100, max: 100, step: 1 },
  { key: 'clarity', label: 'clarity', min: -100, max: 100, step: 1 },
];
const PRESETS: { id: ColourPreset; label: MessageKey }[] = [
  { id: 'tomato', label: 'presetTomato' },
  { id: 'greens', label: 'presetGreens' },
  { id: 'cheese', label: 'presetCheese' },
  { id: 'meat', label: 'presetMeat' },
  { id: 'bread', label: 'presetBread' },
];
const MODES: { id: 'pick' | 'paintIn' | 'paintOut' | 'limit'; label: MessageKey }[] = [
  { id: 'pick', label: 'pickModePick' },
  { id: 'paintIn', label: 'pickModeIn' },
  { id: 'paintOut', label: 'pickModeOut' },
  { id: 'limit', label: 'pickModeLimit' },
];
const defaultLimit = (kind: MaskLimit['kind']): MaskLimit =>
  kind === 'linear'
    ? { kind, geom: { x0: 0.5, y0: 0.3, x1: 0.5, y1: 0.7 } }
    : { kind, geom: { cx: 0.5, cy: 0.5, rx: 0.3, ry: 0.3, angle: 0, feather: 0.5 } };
const NAME: Record<LocalMask['kind'], MessageKey> = {
  linear: 'maskLinear',
  radial: 'maskRadial',
  brush: 'maskBrush',
  colour: 'maskColour',
  object: 'maskObject',
};
const HINT: Record<LocalMask['kind'], MessageKey> = {
  linear: 'maskHintLinear',
  radial: 'maskHintRadial',
  brush: 'maskHintBrush',
  colour: 'maskHintColour',
  object: 'maskHintObject',
};

export function MasksPanel() {
  const t = useT();
  const { settings, update } = useActive();
  const maskId = useUi((s) => s.maskId);
  const overlay = useUi((s) => s.maskOverlay);
  const pickMode = useUi((s) => s.pickMode);
  const objStatus = useObjectMasks((s) => s.status);
  const masks = settings.masks;
  const index = masks.findIndex((m) => m.id === maskId);
  const sel = index >= 0 ? masks[index]! : null;

  const add = (kind: LocalMask['kind']) => {
    if (masks.length >= MAX_MASKS) return;
    const m = newMask(kind);
    update((s) => void s.masks.push(m));
    useUi.getState().set({ maskId: m.id, tool: 'mask', pickMode: 'pick' });
  };
  const pickGeom =
    sel && (sel.kind === 'colour' || sel.kind === 'object') ? (sel.geom as ColourGeom & ObjectGeom) : null;
  const status = sel ? objStatus[sel.id] : undefined;

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{ fontSize: 12 }}>{t('addMaskHint')}</span>
        {(['linear', 'radial', 'brush', 'colour', 'object'] as const).map((k) => (
          <button
            key={k}
            data-testid={`add-mask-${k}`}
            disabled={masks.length >= MAX_MASKS}
            title={masks.length >= MAX_MASKS ? t('maskLimit') : undefined}
            onClick={() => add(k)}
          >
            {t(
              k === 'linear'
                ? 'addLinear'
                : k === 'radial'
                  ? 'addRadial'
                  : k === 'brush'
                    ? 'addBrush'
                    : k === 'colour'
                      ? 'addColour'
                      : 'addObject',
            )}
          </button>
        ))}
      </div>
      {masks.length > 0 && (
        <ul
          role="listbox"
          aria-label={t('panelMasks')}
          style={{ listStyle: 'none', padding: 0, margin: '0 0 6px' }}
        >
          {masks.map((m, i) => {
            const n = masks.slice(0, i + 1).filter((k) => k.kind === m.kind).length;
            return (
              <li key={m.id}>
                <button
                  role="option"
                  aria-selected={m.id === maskId}
                  data-testid={`mask-item-${i}`}
                  onClick={() => useUi.getState().set({ maskId: m.id, tool: 'mask' })}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    ...(m.id === maskId
                      ? { background: 'var(--accent-fill)', color: 'var(--on-accent)' }
                      : {}),
                  }}
                >
                  {t(NAME[m.kind], { n })}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <label style={{ display: 'flex', gap: 6, fontSize: 12, marginBottom: 6 }}>
        <input
          type="checkbox"
          checked={overlay}
          data-testid="mask-overlay"
          onChange={(e) => useUi.getState().set({ maskOverlay: e.target.checked })}
        />
        {t('maskOverlay')}
      </label>
      {sel && (
        <div data-testid="mask-editor">
          <div style={{ fontSize: 12, marginBottom: 6, opacity: 0.8 }}>{t(HINT[sel.kind])}</div>
          {sel.kind === 'brush' && <BrushControls />}
          {pickGeom && (
            <div data-testid="pick-editor">
              <div role="radiogroup" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                {MODES.map((md) => (
                  <button
                    key={md.id}
                    role="radio"
                    aria-checked={pickMode === md.id}
                    data-testid={`pick-mode-${md.id}`}
                    style={
                      pickMode === md.id
                        ? { background: 'var(--accent-fill)', color: 'var(--on-accent)' }
                        : {}
                    }
                    onClick={() => useUi.getState().set({ pickMode: md.id, tool: 'mask' })}
                  >
                    {t(md.label)}
                  </button>
                ))}
              </div>
              {(pickMode === 'paintIn' || pickMode === 'paintOut') && <BrushControls />}
              {pickMode === 'limit' && (
                <div style={{ fontSize: 12, marginBottom: 6 }}>
                  <span>{t('maskLimit2')} </span>
                  <select
                    data-testid="pick-limit"
                    value={pickGeom.limit?.kind ?? 'none'}
                    onChange={(e) => {
                      const v = e.target.value;
                      update((s) => {
                        const g = s.masks[index]!.geom as ColourGeom | ObjectGeom;
                        if (v === 'none') delete g.limit;
                        else g.limit = defaultLimit(v as MaskLimit['kind']);
                      });
                    }}
                  >
                    <option value="none">{t('limitNone')}</option>
                    <option value="linear">{t('limitLinear')}</option>
                    <option value="radial">{t('limitRadial')}</option>
                  </select>
                  {pickGeom.limit && <div style={{ opacity: 0.8, marginTop: 4 }}>{t('maskHintLimit')}</div>}
                </div>
              )}
              {sel.kind === 'colour' && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      gap: 4,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      marginBottom: 6,
                    }}
                  >
                    <span style={{ fontSize: 12 }}>{t('colourPresets')}</span>
                    {PRESETS.map((p) => (
                      <button
                        key={p.id}
                        aria-pressed={pickGeom.preset === p.id}
                        data-testid={`colour-preset-${p.id}`}
                        style={
                          pickGeom.preset === p.id
                            ? { background: 'var(--accent-fill)', color: 'var(--on-accent)' }
                            : {}
                        }
                        onClick={() =>
                          update((s) => {
                            const g = s.masks[index]!.geom as ColourGeom;
                            if (g.preset === p.id) delete g.preset;
                            else {
                              g.preset = p.id;
                              g.range = COLOUR_PRESETS[p.id].range;
                              g.softness = COLOUR_PRESETS[p.id].softness;
                            }
                          })
                        }
                      >
                        {t(p.label)}
                      </button>
                    ))}
                  </div>
                  <Slider
                    label="colourRange"
                    testId="slider-colour-range"
                    value={pickGeom.range}
                    min={0}
                    max={100}
                    step={1}
                    defaultValue={40}
                    onChange={(v, f) =>
                      update((s) => void ((s.masks[index]!.geom as ColourGeom).range = v), f)
                    }
                  />
                  <Slider
                    label="colourSoftness"
                    testId="slider-colour-softness"
                    value={pickGeom.softness}
                    min={0}
                    max={100}
                    step={1}
                    defaultValue={50}
                    onChange={(v, f) =>
                      update((s) => void ((s.masks[index]!.geom as ColourGeom).softness = v), f)
                    }
                  />
                  <div
                    style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, marginBottom: 6 }}
                  >
                    <span data-testid="colour-count">
                      {t('colourPicked', { n: pickGeom.samples.length })}
                    </span>
                    <button
                      disabled={!pickGeom.samples.length}
                      onClick={() => update((s) => void ((s.masks[index]!.geom as ColourGeom).samples = []))}
                    >
                      {t('colourClear')}
                    </button>
                  </div>
                </>
              )}
              {sel.kind === 'object' && (
                <>
                  <Slider
                    label="objectEdge"
                    testId="slider-object-edge"
                    value={pickGeom.edge}
                    min={0}
                    max={100}
                    step={1}
                    defaultValue={50}
                    onChange={(v, f) =>
                      update((s) => void ((s.masks[index]!.geom as ObjectGeom).edge = v), f)
                    }
                  />
                  <div
                    style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, marginBottom: 6 }}
                  >
                    <span data-testid="object-count">{t('objectPoints', { n: pickGeom.points.length })}</span>
                    <button
                      disabled={!pickGeom.points.length}
                      onClick={() => update((s) => void ((s.masks[index]!.geom as ObjectGeom).points = []))}
                    >
                      {t('objectClear')}
                    </button>
                  </div>
                  <div
                    role="status"
                    data-testid="object-status"
                    title={status?.state === 'error' ? status.message : undefined}
                    style={{ fontSize: 12, marginBottom: 6 }}
                  >
                    {status?.state === 'running'
                      ? t('objectRunning')
                      : status?.state === 'missing'
                        ? t('objectMissing')
                        : status?.state === 'error'
                          ? t('objectError', { reason: t(errorKey(status.message ?? '')) })
                          : !pickGeom.points.length
                            ? t('objectFirst')
                            : ''}
                  </div>
                </>
              )}
            </div>
          )}
          {sel.kind === 'radial' && (
            <Slider
              label="maskFeather"
              testId="slider-mask-feather"
              value={Math.round((sel.geom as RadialGeom).feather * 100)}
              min={0}
              max={100}
              step={1}
              defaultValue={50}
              onChange={(v, f) =>
                update((s) => void ((s.masks[index]!.geom as RadialGeom).feather = v / 100), f)
              }
            />
          )}
          {ADJ.map((a) => (
            <Slider
              key={a.key}
              label={a.label}
              testId={`slider-mask-${a.key}`}
              value={sel.adj[a.key]}
              min={a.min}
              max={a.max}
              step={a.step}
              digits={a.digits}
              onChange={(v, f) => update((s) => void (s.masks[index]!.adj[a.key] = v), f)}
            />
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <label style={{ display: 'flex', gap: 6, fontSize: 12, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={sel.invert}
                data-testid="mask-invert"
                onChange={(e) => update((s) => void (s.masks[index]!.invert = e.target.checked))}
              />
              {t('maskInvert')}
            </label>
            <button
              data-testid="mask-delete"
              onClick={() => {
                update((s) => void s.masks.splice(index, 1));
                useUi.getState().set({ maskId: null, tool: 'none' });
              }}
            >
              {t('maskDelete')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
